import { Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const { Text } = Typography;

export type AiOutputLanguage = 'Vietnamese' | 'English';

export const getDefaultAiOutputLanguage = (locale?: string): AiOutputLanguage =>
  locale?.toLowerCase().startsWith('vi') ? 'Vietnamese' : 'English';

interface AiOutputLanguageSelectorProps {
  value: AiOutputLanguage;
  onChange: (language: AiOutputLanguage) => void;
  disabled?: boolean;
  className?: string;
}

const languageOptions: Array<{
  value: AiOutputLanguage;
  translationKey: string;
}> = [
  { value: 'Vietnamese', translationKey: 'ai_language_vietnamese' },
  { value: 'English', translationKey: 'ai_language_english' },
];

const AiOutputLanguageSelector = ({
  value,
  onChange,
  disabled = false,
  className,
}: AiOutputLanguageSelectorProps) => {
  const { t } = useTranslation(['recruitment']);
  const selectLanguage = (language: AiOutputLanguage) => {
    if (!disabled) onChange(language);
  };

  return (
    <LanguageSelector
      className={className}
      role="radiogroup"
      aria-label={t('recruitment:ai_output_language')}
      aria-disabled={disabled}
    >
      <Text>{t('recruitment:ai_output_language')}</Text>
      <TagGroup>
        {languageOptions.map((option) => (
          <LanguageTag
            key={option.value}
            checked={value === option.value}
            role="radio"
            aria-checked={value === option.value}
            tabIndex={disabled ? -1 : 0}
            className={disabled ? 'is-disabled' : undefined}
            onChange={() => selectLanguage(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectLanguage(option.value);
              }
            }}
          >
            {t(`recruitment:${option.translationKey}`)}
          </LanguageTag>
        ))}
      </TagGroup>
    </LanguageSelector>
  );
};

export default AiOutputLanguageSelector;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const TagGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const LanguageTag = styled(Tag.CheckableTag)`
  padding: 3px 12px;
  margin: 0;
  color: var(--cl_neutral500);
  border: 1px solid var(--cl_neutral100);
  border-radius: var(--bd_radius_default);
  background: var(--cl_white);
  cursor: pointer;

  &.ant-tag-checkable-checked {
    color: var(--cl_primary500);
    border-color: var(--cl_primary500);
    background: rgba(241, 89, 42, 0.08);
  }

  &.is-disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
